import React, { useCallback, useEffect, useState } from 'react';
import { Tabs, Tab, Container, Row } from 'react-bootstrap';
import dayjs from 'dayjs';
import Helmet from 'react-helmet';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilterList } from '@fortawesome/pro-solid-svg-icons';

import { ApiServiceServerless } from '../../xhr_libs';

import UtilitySummary from './UtilitySummary';
import UtilityTrends from './UtilityTrends';
import CarbonSummary from './CarbonSummary';
import CHARTCOLORS from '../../helpers/chartColors';
import { useTabUrl } from '../../helpers/tracked';
import DashboardDateSelectContainer from './DashboardDateSelectContainer';
import Loader from '../../components/Loader';
import { getUserOrganizationPreference } from '../../helpers/user-preferences';
import BuildingSelectModal from '../../components/modals/BuildingSelectModal';
import SolarSummary from './SolarSummary';
import { dashboardMerge, defaultDashboardInfo } from './helpers';

const PAGE_TITLE = 'Dashboard';


function Dashboard(props) {
  const {
    userSelectedOrganization,
    userSelectedOrganizationDetails,
    setDisableOrganizationSelect,
    showToast,
  } = props;

  const [dashboardData, setDashboardData] = useState({});
  const [dashboardInfo, setDashboardInfo] = useState(defaultDashboardInfo);

  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [dateOption, setDateOption] = useState(1);

  const [activeIds, setActiveIds] = useState([]);
  const [activeKeys, setActiveKeys] = useState([]);
  const [localBuildingInfo, setLocalBuildingInfo] = useState({});
  const [localBuildingIds, setLocalBuildingIds] = useState([]);

  const [isLoading, setIsLoading] = useState(true);

  const [showBuildingModal, setShowBuildingModal] = useState(false);

  const [activeTab, setActiveTab] = useTabUrl('utility-summary');
  const [activeTabList, setActiveTabList] = useState(['building-date']);


  useEffect(() => {
    setActiveIds([]);
    setActiveKeys([]);
    setLocalBuildingInfo({});
    setLocalBuildingIds([]);
  }, [userSelectedOrganization]);

  useEffect(() => {
    if (dashboardInfo['building_info']) {
      let temp_building_info = { ...dashboardInfo['building_info'] };
      let temp_building_ids = [];
      Object.values(temp_building_info).forEach((building) => {
        temp_building_ids.push(building.id);
      });
      setLocalBuildingIds(temp_building_ids);
      setLocalBuildingInfo(temp_building_info);
    } else {
      setLocalBuildingInfo({});
      setLocalBuildingIds([]);
    }
  }, [dashboardInfo]);

  useEffect(() => {
    let temp_prefered_buildings = [];
    const prefered_buildings = getUserOrganizationPreference(
      'dbs',
      userSelectedOrganization.id
    );
    if (
      prefered_buildings &&
      Array.isArray(prefered_buildings) &&
      prefered_buildings.length > 0
    ) {
      prefered_buildings.forEach((building) => {
        if (localBuildingIds.includes(building)) {
          temp_prefered_buildings.push(building);
        }
      });
      setActiveIds([...temp_prefered_buildings]);
    } else {
      setActiveIds([...localBuildingIds]);
    }
  }, [localBuildingIds, userSelectedOrganization.id]);

  useEffect(() => {
    let new_keys = [];
    activeIds.forEach((id) => {
      if (id in dashboardInfo['building_info']) {
        new_keys.push(dashboardInfo['building_info'][id]['name']);
      }
    });
    setActiveKeys(new_keys);
  }, [activeIds, dashboardInfo]);

  useEffect(() => {
    setDisableOrganizationSelect(isLoading);
  }, [isLoading, setDisableOrganizationSelect]);

  const addNewActiveTab = useCallback(
    (tab) => {
      if (tab !== activeTab) {
        setActiveTab(tab);
      }
      if (!activeTabList.includes(tab)) {
        let temp_keys = [...activeTabList];
        temp_keys.push(tab);
        setActiveTabList(temp_keys);
      }
    },
    [activeTab, activeTabList, setActiveTab]
  );

  useEffect(() => {
    addNewActiveTab(activeTab);
  }, [activeTab, addNewActiveTab]);

  const [activeGHGLicense, setActiveGHGLicense] = useState(false);
  const [activeSolarLicense, setActiveSolarLicense] = useState(false);
  useEffect(() => {
    let tempGHGLicense = false;
    let tempSolarLicense = false;
    if (userSelectedOrganizationDetails.id) {
      if (
        userSelectedOrganizationDetails &&
        userSelectedOrganizationDetails.licenses &&
        userSelectedOrganizationDetails.licenses.ghg
      ) {
        tempGHGLicense = true;
      }
      if (
        userSelectedOrganizationDetails &&
        userSelectedOrganizationDetails.licenses &&
        userSelectedOrganizationDetails.licenses.solar
      ) {
        tempSolarLicense = true;
      }
    }
    setActiveGHGLicense(tempGHGLicense);
    setActiveSolarLicense(tempSolarLicense);
  }, [userSelectedOrganizationDetails]);

  useEffect(() => {
    if (activeTab === 'solar-summary' && !activeSolarLicense) {
      addNewActiveTab('utility-summary');
    }
  }, [activeTab, activeSolarLicense, addNewActiveTab]);

  const getDashboardData = useCallback(
    (orgId, endDate, dateOption, page) => {
      setIsLoading(true);
      ApiServiceServerless.get(
        `/dashboard/${orgId}/${dayjs
          .utc(endDate)
          .format('YYYY-MM-DD')}/${dateOption}/${page}`,
        { authorization_id: orgId }
      )
        .then(({ data }) => {
          setDashboardData((prev) => dashboardMerge(prev, data.dashboard_data))
          if (data.next_page !== 0) {
            getDashboardData(orgId, endDate, dateOption, data.next_page)
          }
          else {
            setIsLoading(false)
          }
        })
        .catch((err) => {
          setIsLoading(false)
          showToast('danger', 'Error', err);
          throw err;
        })
    },
    [setIsLoading, showToast]
  );

  useEffect(() => {
    if (userSelectedOrganization.id && endDate) {
      setDashboardData({})
      getDashboardData(userSelectedOrganization.id, endDate, dateOption, 0, {});
    }
  }, [userSelectedOrganization.id, endDate, dateOption, getDashboardData]);

  const getDashboardInfo = useCallback(
    (orgId) => {
      ApiServiceServerless.get(`/dashboard/${orgId}/info`, {
        authorization_id: orgId,
      })
        .then((res) => {
          setDashboardInfo(res.data)
        })
        .catch((err) => {
          setIsLoading(false);
          throw err;
        });
    }, [])

  const getEndDate = useCallback(
    (orgId) => {
      if (endDate) return endDate;
      setIsLoading(true);
      ApiServiceServerless.get(`/dashboard/${orgId}`, {
        authorization_id: orgId,
      })
        .then((res) => {
          let end_date = dayjs.utc();
          if (res.data) {
            end_date = dayjs.utc(res.data);
          }
          let start_date = end_date;
          start_date = start_date
            .month(start_date.month() - 11)
            .startOf('month');

          setEndDate(end_date);
          setStartDate(start_date);
          return end_date;
        })
        .catch((err) => {
          setIsLoading(false);
          throw err;
        });
    },
    [setIsLoading, endDate]
  );

  useEffect(() => {
    if (!userSelectedOrganization.id) {
      setDashboardData({});
      setDashboardInfo(defaultDashboardInfo)
      setEndDate();
      setIsLoading(false);
    } else {
      getEndDate(userSelectedOrganization.id);
      getDashboardInfo(userSelectedOrganization.id)
    }
  }, [userSelectedOrganization.id, getEndDate, getDashboardInfo]);

  return (
    <>
      <Container className='et-container et-main-tab-content'>
        <Helmet>
          <title>{PAGE_TITLE}</title>
        </Helmet>

        <Row
          style={{
            display: 'flex',
            float: 'right',
            width: '425px',
            marginRight: '-1em',
            marginTop: '0.5em',
          }}
          className='et-dashboard-date-select-cont'
        >
          <DashboardDateSelectContainer
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            dateOption={dateOption}
            setDateOption={setDateOption}
            isLoading={isLoading}
          />

          <FontAwesomeIcon
            style={{
              fontSize: '22px',
              marginLeft: '0.5em',
              color: CHARTCOLORS[1],
              cursor: 'pointer',
              position: 'relative',
              top: '3px',
              right: '20px',
            }}
            icon={faFilterList}
            id={`report-building-select-popover`}
            onClick={() => setShowBuildingModal(true)}
            title='Select Buildings'
          />
        </Row>
        <Tabs
          id='dashboard-tabs'
          activeKey={activeTab}
          onSelect={(e) => addNewActiveTab(e)}
          className='et-main-tabs'
        >
          <Tab eventKey='utility-summary' title={'Utility Summary'}>
            {activeTabList.includes('utility-summary') && (
              <>
                {' '}
                {isLoading ? (
                  <Loader />
                ) : (
                  <UtilitySummary
                    dashboardData={dashboardData}
                    dashboardInfo={dashboardInfo}
                    organization={userSelectedOrganization}
                    open={activeTab === 'utility-summary'}
                    activeIds={activeIds}
                    localBuildingInfo={localBuildingInfo}
                    localBuildingIds={localBuildingIds}
                  />
                )}
              </>
            )}
          </Tab>


          <Tab eventKey='utility-trends' title={'Utility Trends'}>
            {activeTabList.includes('utility-trends') && (
              <>
                {' '}
                {isLoading ? (
                  <Loader />
                ) : (
                  <UtilityTrends
                    dashboardData={dashboardData}
                    dashboardInfo={dashboardInfo}
                    organization={userSelectedOrganization}
                    endDate={endDate}
                    dateOption={dateOption}
                    activeIds={activeIds}
                    activeKeys={activeKeys}
                    localBuildingInfo={localBuildingInfo}
                    localBuildingIds={localBuildingIds}
                    selectedBuildings={activeIds}
                    open={activeTab === 'utility-trends'}
                    activeGHGLicense={activeGHGLicense}
                  />
                )}
              </>
            )}
          </Tab>

          <Tab eventKey='carbon-summary' title={'Carbon Summary'}>
            {activeTabList.includes('carbon-summary') && (
              <>
                {isLoading ? (
                  <Loader />
                ) : (
                  <CarbonSummary
                    isLoading={isLoading}
                    dashboardData={dashboardData}
                    dashboardInfo={dashboardInfo}
                    organization={userSelectedOrganization}
                    endDate={endDate}
                    dateOption={dateOption}
                    activeIds={activeIds}
                    activeKeys={activeKeys}
                    localBuildingInfo={localBuildingInfo}
                    localBuildingIds={localBuildingIds}
                    activeGHGLicense={activeGHGLicense}
                    open={activeTab === 'carbon-summary'}
                  />
                )}
              </>
            )}
          </Tab>
          {activeSolarLicense && (
            <Tab eventKey='solar-summary' title={'Solar Summary'}>
              {activeTabList.includes('solar-summary') && (
                <>
                  {isLoading ? (
                    <Loader />
                  ) : (
                    <SolarSummary
                      isLoading={isLoading}
                      dashboardData={dashboardData}
                      endDate={endDate}
                      dateOption={dateOption}
                      organization={userSelectedOrganization}
                      activeIds={activeIds}
                      localBuildingInfo={localBuildingInfo}
                      localBuildingIds={localBuildingIds}
                      open={activeTab === 'solar-summary'}
                    />
                  )}
                </>
              )}
            </Tab>
          )}
        </Tabs>
      </Container>
      <BuildingSelectModal
        show={showBuildingModal}
        onHide={() => setShowBuildingModal(false)}
        buildings={localBuildingInfo}
        buildingIds={localBuildingIds}
        selectedBuildings={activeIds}
        setSelectedBuildings={setActiveIds}
        setNonSelectedBuildings={() => { }}
        organization_id={userSelectedOrganization.id}
        preference_key={'dbs'}
      />
    </>
  );
}

export default Dashboard;
